import { useState } from "react";
import { getRules, deleteRule } from "@/services/ruleService";
import { Rule } from "@/lib/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export function useMailboxRules(onMailboxRulesCountChange: (mailboxId: string, count: number) => void) {
  const { tenantId } = useAuth();
  const [mailboxRules, setMailboxRules] = useState<{[key: string]: Rule[]}>({});
  const [isRulesLoading, setIsRulesLoading] = useState<{[key: string]: boolean}>({});
  const [expandedMailboxes, setExpandedMailboxes] = useState<Set<string>>(new Set());
  
  const handleMailboxToggle = async (mailboxId: string) => {
    const newExpandedMailboxes = new Set(expandedMailboxes);
    
    if (newExpandedMailboxes.has(mailboxId)) {
      newExpandedMailboxes.delete(mailboxId);
    } else {
      newExpandedMailboxes.add(mailboxId);
      
      if (!mailboxRules[mailboxId]) {
        await loadMailboxRules(mailboxId);
      }
    }
    
    setExpandedMailboxes(newExpandedMailboxes);
  };

  const loadMailboxRules = async (mailboxId: string) => {
    setIsRulesLoading(prev => ({ ...prev, [mailboxId]: true }));
    
    try {
      const rules = await getRules(tenantId, mailboxId);
      setMailboxRules(prev => ({
        ...prev,
        [mailboxId]: rules
      }));
      
      // Update the mailbox rules count in the parent component
      onMailboxRulesCountChange(mailboxId, rules.length);
      
      return rules;
    } catch (error) {
      console.error("Error loading rules:", error);
      toast.error("Failed to load rules");
      return [];
    } finally {
      setIsRulesLoading(prev => ({ ...prev, [mailboxId]: false }));
    }
  };

  const handleRuleUpdated = async (mailboxId: string): Promise<void> => {
    const rules = await loadMailboxRules(mailboxId);
    onMailboxRulesCountChange(mailboxId, rules.length);
  };

  const handleReorderRules = async (mailboxId: string, updatedRules: Rule[]) => {
    setMailboxRules(prev => ({
      ...prev,
      [mailboxId]: updatedRules
    }));
    
    // Update the mailbox rules count
    onMailboxRulesCountChange(mailboxId, updatedRules.length);
  };

  const handleDeleteRule = async (rule: Rule) => {
    const success = await deleteRule(rule.id);
    if (success && rule.mailbox_id) {
      await handleRuleUpdated(rule.mailbox_id);
      toast.success("Rule deleted successfully");
      return true;
    }
    return false;
  };

  return {
    mailboxRules,
    isRulesLoading,
    expandedMailboxes,
    handleMailboxToggle,
    loadMailboxRules,
    handleRuleUpdated,
    handleReorderRules,
    handleDeleteRule
  };
}
